/**
 * src/config/navConfig.js
 *
 * Sidebar navigation structure.
 * Items are filtered at render time by module permissions from auth.js.
 * Permission data (who can access what) lives in the database — this file
 * only defines what modules exist and how they are grouped.
 *
 * ── INFORMATION ARCHITECTURE PRINCIPLES ──────────────────────────────────────
 *
 * Primary navigation = work domains ("what kind of work am I doing?").
 * NOT frameworks, methods, or regulations.
 *
 * Tabs = perspectives on the same data object, not separate features.
 *
 * Executive View = role-based presentation overlay → lives under Reporting,
 * not alongside operational work.
 *
 * Output ≠ Work: exports and reporting belong under Reporting, not inside
 * operational modules.
 *
 * Shared objects, multiple entry points: a risk may appear in Risk, Assurance,
 * and Reporting. A vendor appears in Assets & Parties but impacts Risk and Privacy.
 * Avoid artificial silos.
 *
 * ── DOMAIN MAP ───────────────────────────────────────────────────────────────
 *
 *  WORK          — cross-cutting operational work (tasks, schedule, guidance)
 *  GOVERNANCE    — managing and demonstrating compliance (controls, docs, SoA)
 *  RISK          — identifying, assessing, and treating risks and threats
 *  ASSETS &      — what we protect (assets) and who we work with (vendors,
 *  PARTIES         employees)
 *  ASSURANCE     — testing and verifying that controls work (audits, findings,
 *                  gap analysis, privacy / GDPR compliance)
 *  RESILIENCE    — preparing for, responding to, and recovering from disruptions
 *                  (BIA, BCDR, incidents)
 *
 * ── FUTURE ARCHITECTURE NOTES ────────────────────────────────────────────────
 *
 * Pen Tests: currently modelled inside BCDR (bcdrPenTests in app state).
 * Conceptually they are an Assurance activity (security testing). A future
 * data-model migration should move them to the Assurance domain alongside
 * audits and gap analyses.
 *
 * Data Export: currently a Settings tab. As Reporting grows it should move here.
 *
 * When adding a new module always ask: "Which work domain does this belong to
 * — and why?" Place it in the domain whose primary question it answers.
 *
 * ── GROUP ICONS ──────────────────────────────────────────────────────────────
 *
 * Each group has an `icon` field used in the rail sidebar mode. Pick a rune
 * that is semantically distinct from item-level icons where possible.
 */

export const NAV_GROUPS = [
  // ── 1. WORK ──────────────────────────────────────────────────────────────
  // Cross-cutting operational layer. Answers: "What do I need to do today?"
  {
    key: 'work',
    label: 'Work',
    icon: 'ᛏ', // Tiwaz — action, will, day-to-day effort
    color: '#60a5fa', // blue — everyday, clear, present
    items: [
      { key: 'dashboard', path: '/dashboard', icon: 'ᛞ', label: 'Dashboard' },
      { key: 'tasks', path: '/tasks', icon: 'ᛏ', label: 'Tasks' },
      { key: 'calendar', path: '/calendar', icon: 'ᚲ', label: 'Calendar' },
      { key: 'nextsteps', path: '/next-steps', icon: 'ᚾ', label: 'Next Steps' },
      { key: 'guides', path: '/guides', icon: 'ᚷ', label: 'Guides' },
    ],
  },

  // ── 2. GOVERNANCE ────────────────────────────────────────────────────────
  // Managing, documenting, and demonstrating compliance.
  // Answers: "Are our controls defined, implemented, and documented?"
  {
    key: 'governance',
    label: 'Governance',
    icon: 'ᚷ', // Gebo — covenant, obligation, the compliance gift
    color: '#818cf8', // indigo — formal, structured, authoritative
    items: [
      { key: 'controls', path: '/controls', icon: 'ᚲ', label: 'Controls' },
      { key: 'documents', path: '/documents', icon: 'ᛞ', label: 'Documents' },
      { key: 'soa', path: '/soa', icon: 'ᛋ', label: 'Applicability' },
      { key: 'artefacts', path: '/artefacts', icon: 'ᚨ', label: 'Artefacts' },
      { key: 'products', path: '/products', icon: 'ᛈ', label: 'Products' },
      { key: 'management-review', path: '/management-review', icon: 'ᛗ', label: 'Mgt Review' },
    ],
  },

  // ── 3. RISK ──────────────────────────────────────────────────────────────
  // Identifying, assessing, and treating risks and threats.
  // Answers: "What could go wrong, and what are we doing about it?"
  {
    key: 'risk',
    label: 'Risk',
    icon: 'ᚱ', // Raidho — journey into the unknown, risk-taking
    color: '#fb923c', // orange — warning, caution, heat
    items: [
      { key: 'risks', path: '/risks', icon: 'ᚱ', label: 'Risk Register' },
      { key: 'threats', path: '/threats', icon: 'ᚦ', label: 'Threats' },
      { key: 'vulnerabilities', path: '/vulnerabilities', icon: 'ᛉ', label: 'Vulnerabilities' },
      { key: 'priority', path: '/priority', icon: 'ᛇ', label: 'Priority' },
      {
        key: 'ai-governance',
        path: '/ai-governance',
        icon: 'ᚨ',
        label: 'AI Governance',
        releaseFlag: 'aiGovernance',
      },
    ],
  },

  // ── 5. ASSETS & PARTIES ──────────────────────────────────────────────────
  // What we protect and who we work with.
  // Answers: "What do we have, and who are we responsible for / dependent on?"
  //
  // Note: Vendors have a risk dimension (third-party risk assessments) and a
  // privacy dimension (data processor agreements). They live here as the primary
  // register; risk and privacy views reference them as a shared object.
  {
    key: 'assets-parties',
    label: 'Assets & Parties',
    icon: 'ᚹ', // Wunjo — community, shared things, parties
    color: '#38bdf8', // sky — open, managed, surveyed
    items: [
      { key: 'assets', path: '/assets', icon: 'ᚨ', label: 'Assets' },
      { key: 'vendors', path: '/vendors', icon: 'ᚹ', label: 'Vendors' },
      // Customers, Delivery Agreements and Subcontractors are one tabbed page
      // (SupplierChainHub) because they are one ordered sequence: a customer
      // must exist before its delivery agreement, and a delivery agreement
      // before any subcontractor involvement on it. Two sibling nav items hid
      // that ordering and left Subcontractors looking like a dead end.
      { key: 'customers', path: '/customers', icon: 'ᛗ', label: 'Delivery Chain' },
      { key: 'agreements-register', path: '/agreements-register', icon: 'ᛟ', label: 'Contracts' },
      { key: 'employees', path: '/employees', icon: 'ᛖ', label: 'Employees' },
      // hrOnly: true — only shown to users with hrRecruitment permission
      { key: 'candidates', path: '/candidates', icon: 'ᚲ', label: 'Candidates', hrOnly: true },
    ],
  },

  // ── 6. ASSURANCE ─────────────────────────────────────────────────────────
  // Testing, auditing, and verifying that controls actually work.
  // Answers: "How do we know our controls are effective?"
  //
  // Gap Analysis moved here from Governance: it is an assessment activity
  // (readiness check, Cl. 9.1 monitoring), not ongoing governance work.
  //
  // Findings includes audit programmes and internal audits (all audit-related
  // objects share the same register with different perspectives as tabs).
  //
  // Privacy moved here from its own group: DPIAs are impact assessments, DSRs
  // are verification activities, and the overall GDPR compliance programme is
  // best understood as an assurance domain alongside audits and gap analyses.
  {
    key: 'assurance',
    label: 'Assurance',
    icon: 'ᚠ', // Fehu — wealth testing, verification of value
    color: '#4ade80', // green — verified, safe, assured
    items: [
      { key: 'gap', path: '/gap', icon: 'ᚨ', label: 'Assessments' },
      { key: 'findings', path: '/findings', icon: 'ᚠ', label: 'Audits & Findings' },
      { key: 'privacy', path: '/privacy', icon: 'ᛈ', label: 'Privacy' },
      { key: 'grc', path: '/grc', icon: 'ᛜ', label: 'Audit Engagement' },
    ],
  },

  // ── 6. RESILIENCE ────────────────────────────────────────────────────────
  // Preparing for, responding to, and recovering from disruptions.
  // Answers: "What happens when something goes wrong, and can we recover?"
  //
  // Incidents moved here from Risk & Assurance: incident response is a
  // resilience/continuity activity with its own detection-response-recovery
  // lifecycle, distinct from risk identification and treatment.
  //
  // BIA and BCDR moved here from People & Assets: business continuity is
  // a resilience domain, not an asset management domain.
  {
    key: 'resilience',
    label: 'Resilience',
    icon: 'ᚻ', // Hagalaz — disruption, the storm you prepare for
    color: '#f87171', // red — danger, disruption, emergency
    items: [
      { key: 'bia', path: '/bia', icon: 'ᛒ', label: 'Business Impact' },
      { key: 'bcdr', path: '/bcdr', icon: 'ᚻ', label: 'BC/DR' },
      { key: 'incidents', path: '/incidents', icon: 'ᛁ', label: 'Incidents' },
    ],
  },
];

// ── System ───────────────────────────────────────────────────────────────────
// Technical configuration, integrations, and admin tools. Not operational work.
// Frameworks lives here: it is a control-library configuration activity
// (you configure frameworks once; the actual compliance work happens in Controls).
// Items with adminOnly: true are only shown to admin-role users.
export const SYSTEM_NAV = [
  // Support is never adminOnly and never plan-gated — anybody who can open the
  // app can reach us (SUP-B-002 decides what they SEE, not whether they can ask).
  { key: 'support', path: '/support', icon: 'ᛊ', label: 'Support' },
  { key: 'integrations', path: '/integrations', icon: 'ᛃ', label: 'Integrations' },
  { key: 'frameworks', path: '/frameworks', icon: 'ᚠ', label: 'Frameworks' },
  {
    key: 'email-breach-monitoring',
    path: '/breach-monitor',
    icon: 'ᛒ',
    label: 'Breach Monitor',
    adminOnly: true,
  },
  // msr 4.10a: the executive index (Position, Trajectory, Confidence),
  // tenant-facing only while the server's MSR_PUBLISH_ENABLED switch is on
  // (D26) — `requiresMsrPublish` hides it otherwise (navRoleAllowed). Admin
  // only, behind the `improvement` role module and plan gate (navModuleKey).
  {
    key: 'improvement-index',
    moduleKey: 'improvement',
    releaseFlag: 'ismsImprovement',
    path: '/improvement',
    icon: 'ᛁ',
    label: 'Improvement',
    adminOnly: true,
    requiresMsrPublish: true,
  },
  // Internal measurement-programme tool (msr 2.7, GH #7130). Admin only, and
  // behind the `improvement` role module and plan gate — the executive
  // improvement view (Phase 4) will get its own entry.
  {
    key: 'improvement',
    path: '/improvement/explorer',
    releaseFlag: 'ismsImprovement',
    icon: 'ᛗ',
    label: 'Metric Explorer',
    adminOnly: true,
  },
  // msr 4.3: the Context Change Register. `moduleKey` makes it answer to the
  // `improvement` role module and plan gate like the Explorer (navModuleKey);
  // not admin-only — any tenant user may read it, and the approver of a
  // "no change needed" request may be a non-admin Approver.
  {
    key: 'context-changes',
    moduleKey: 'improvement',
    releaseFlag: 'ismsImprovement',
    path: '/improvement/context-changes',
    icon: 'ᚲ',
    label: 'Context Changes',
  },
  // msr 4.4: information security objectives (ISO 27001 6.2). Answers to the
  // `improvement` role module and plan gate (navModuleKey). Admins set and
  // review them; managers read them (founder decision B on PR #7407) —
  // `roles` names the non-admin roles that also see the entry (navRoleAllowed).
  {
    key: 'objectives',
    moduleKey: 'improvement',
    releaseFlag: 'ismsImprovement',
    path: '/improvement/objectives',
    icon: 'ᛟ',
    label: 'Objectives',
    roles: ['manager'],
  },
  // PDWB-01: policy-driven workflows (docs/workflow-engine-design.md). Admins
  // design and publish them; managers read them (workflow-definitions-read
  // serves both) — `roles` names the non-admin role that also sees the entry.
  {
    key: 'workflows',
    path: '/workflows',
    icon: 'ᚹ',
    label: 'Workflows',
    roles: ['manager'],
    releaseFlag: 'policyWorkflows',
  },
  { key: 'users', path: '/users', icon: 'ᚢ', label: 'Users', adminOnly: true },
  { key: 'log', path: '/log', icon: 'ᛚ', label: 'Activity Log', adminOnly: true },
  {
    key: 'security-events',
    path: '/security-events',
    icon: 'ᛉ',
    label: 'Security Events',
    adminOnly: true,
  },
];

// ── Rail group meta (used in rail sidebar mode) ───────────────────────────────
export const SYSTEM_GROUP = { key: 'system', label: 'System', icon: 'ᛋ', color: '#94a3b8' }; // Sowilo — slate/neutral

/**
 * May this role see the item? `adminOnly` items: admins only. Items naming
 * `roles`: admins plus those roles (msr 4.4: Objectives for managers). Any
 * other item: everyone the module and plan gates let through.
 */
export function navRoleAllowed(item, { canAdmin, role, msrPublish, releasedFlags } = {}) {
  // msr 4.10a: shown only on an explicit 'on' from the server (fail-closed).
  if (item && item.requiresMsrPublish && msrPublish !== 'on') return false;
  if (!navReleaseAllowed(item, releasedFlags)) return false;
  if (item && item.adminOnly) return Boolean(canAdmin);
  if (item && Array.isArray(item.roles)) return Boolean(canAdmin) || item.roles.includes(role);
  return true;
}

/**
 * RPDA-04: an item that declares `releaseFlag: '<key>'` is shown only when the
 * server resolved that release flag ON for this tenant (useReleasedFlags —
 * fail-closed: loading, a failed read and an unknown key all hide it). An
 * item without one is unaffected. This is an AND with every other gate, so a
 * release never shows what the plan or the role denies (RFLG-B-004).
 */
export function navReleaseAllowed(item, releasedFlags) {
  if (!item || !item.releaseFlag) return true;
  return (
    !!releasedFlags &&
    typeof releasedFlags.has === 'function' &&
    releasedFlags.has(item.releaseFlag)
  );
}

/**
 * The role-module / plan-gate key a nav item answers to: its own key, unless
 * it declares `moduleKey` (an extra screen of an existing module, msr 4.3).
 */
export function navModuleKey(item) {
  return (item && item.moduleKey) || (item && item.key);
}
